import React from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { useDispatch } from 'react-redux'

import { tokens } from '../../theme'
import { dismissSyncNotice } from '../../redux/sync/actions'
import {
  SUPPORT_WHATSAPP_LABEL,
  SUPPORT_WHATSAPP_URL,
} from '../../utils/syncPanelModel'
import { useSyncPanelModel } from './useSyncPanelModel'

// Só para leitor de tela. Em px de propósito: no `sx` do MUI, `width: 1` é 100%, e o span
// absoluto media o Paper inteiro, gerando as barras de rolagem do painel (QA HML U1).
const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
}

const STATUS = {
  done: {
    Icon: CheckCircleIcon,
    color: tokens.semantic.success.main,
    label: 'Concluído',
  },
  active: {
    Icon: AutorenewIcon,
    color: tokens.semantic.warning.main,
    label: 'Em andamento',
  },
  waiting: {
    Icon: RadioButtonUncheckedIcon,
    color: '#8A8A8A',
    label: 'Aguardando',
  },
  estimate: {
    Icon: HourglassEmptyIcon,
    color: tokens.semantic.info.main,
    label: 'Estimativa',
  },
  attention: {
    Icon: WarningAmberIcon,
    color: tokens.semantic.warning.main,
    label: 'Atenção',
  },
  unknown: { Icon: HelpOutlinedIcon, color: '#8A8A8A', label: 'Sem leitura' },
}

// O texto vem pronto do modelo; aqui só o número do WhatsApp vira link.
function RefusedWarningText({ text }) {
  const at = text.indexOf(SUPPORT_WHATSAPP_LABEL)
  if (at < 0) return text
  return (
    <>
      {text.slice(0, at)}
      <a href={SUPPORT_WHATSAPP_URL} target='_blank' rel='noopener noreferrer'>
        {SUPPORT_WHATSAPP_LABEL}
      </a>
      {text.slice(at + SUPPORT_WHATSAPP_LABEL.length)}
    </>
  )
}

export function SyncSteps({ steps, compact = false }) {
  return (
    <Box
      component='ol'
      data-testid='sync-steps'
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'grid',
        gap: compact ? 0.75 : 1.5,
      }}
    >
      {steps.map((s) => {
        const { Icon, color, label } = STATUS[s.status]
        return (
          <Box
            component='li'
            key={s.id}
            data-step={s.id}
            data-status={s.status}
            aria-current={s.status === 'active' ? 'step' : undefined}
            sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}
          >
            <Icon
              aria-hidden='true'
              sx={{ color, fontSize: 22, mt: '1px', flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {s.number}. {s.title}
                <Box component='span' sx={VISUALLY_HIDDEN}>
                  {' '}
                  — {label}
                </Box>
              </Typography>
              {!compact && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block' }}
                >
                  {s.detail}
                </Typography>
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export function SyncProgressBlock({ progress }) {
  return (
    <Box data-testid='sync-progress' sx={{ display: 'grid', gap: 0.75 }}>
      <Typography variant='body2' sx={{ fontWeight: 600 }}>
        {progress.title}
      </Typography>
      {progress.entityLabel && (
        <Typography variant='caption'>
          Gravando: {progress.entityLabel}
          {progress.chunkLabel ? ` · ${progress.chunkLabel}` : ''}
        </Typography>
      )}
      <LinearProgress
        aria-label='Progresso da sincronização'
        variant={progress.fraction == null ? 'indeterminate' : 'determinate'}
        value={
          progress.fraction == null
            ? undefined
            : Math.round(progress.fraction * 100)
        }
      />
      {progress.recordsLabel && (
        <Typography variant='caption'>{progress.recordsLabel}</Typography>
      )}
      {progress.retryLabel && (
        <Typography variant='caption'>{progress.retryLabel}</Typography>
      )}
      {progress.hint && (
        <Typography variant='caption' color='text.secondary'>
          {progress.hint}
        </Typography>
      )}
      {progress.elapsedLabel && (
        <Typography variant='caption'>
          Tempo decorrido: {progress.elapsedLabel}
        </Typography>
      )}
      <Typography variant='caption' sx={{ fontWeight: 600 }}>
        {progress.keepOpenWarning}
      </Typography>
    </Box>
  )
}

export default function SyncPanelDialog({ open, onClose, snapshot, now }) {
  const dispatch = useDispatch()
  const model = useSyncPanelModel(snapshot, {
    tickMs: open ? 15000 : 0,
    now,
  })
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      aria-labelledby='sync-panel-title'
    >
      <DialogTitle id='sync-panel-title'>Sincronização</DialogTitle>
      <DialogContent dividers>
        <Typography data-testid='panel-line' sx={{ fontWeight: 700, mb: 1.5 }}>
          {model.line.text}
        </Typography>
        {model.notice && (
          <Alert
            severity={model.notice.severity}
            sx={{ mb: 1.5 }}
            action={
              <Button
                color='inherit'
                size='small'
                onClick={() => dispatch(dismissSyncNotice())}
              >
                Entendi
              </Button>
            }
          >
            {model.notice.text}
          </Alert>
        )}
        {model.refusedWarning && (
          <Alert severity='warning' sx={{ mb: 1.5 }}>
            <RefusedWarningText text={model.refusedWarning} />
          </Alert>
        )}
        {model.progress && (
          <Box sx={{ mb: 2 }}>
            <SyncProgressBlock progress={model.progress} />
          </Box>
        )}
        <SyncSteps steps={model.steps} />
        {model.lastPullText && (
          <Typography variant='caption' sx={{ display: 'block', mt: 2 }}>
            {model.lastPullText}
          </Typography>
        )}
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mt: 1 }}
        >
          {model.footnote}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}
